/**
 * Phase 9A — Clinic Document Manager
 * 
 * Admin/clerk-facing document triage dashboard.
 * Three lanes: High Confidence (green) | Medium (yellow) | Low (red)
 * AI classifies + matches → clerk confirms → routes to physician My Day
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  FileText, Check, Loader2, AlertTriangle, ChevronDown, Send,
  FlaskConical, Stethoscope, Building, Shield, Pill, X, Eye,
  ArrowRight, CheckCircle2, ScanLine, Upload, UserPlus, Maximize2,
  User, ChevronLeft, ChevronRight, Search
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  lab_result: { label: 'Lab Result', icon: FlaskConical, color: 'text-blue-500 bg-blue-50' },
  imaging_report: { label: 'Imaging', icon: ScanLine, color: 'text-violet-500 bg-violet-50' },
  consult_note: { label: 'Consult Note', icon: Stethoscope, color: 'text-emerald-500 bg-emerald-50' },
  hospital_record: { label: 'Hospital Record', icon: Building, color: 'text-red-500 bg-red-50' },
  referral: { label: 'Referral', icon: ArrowRight, color: 'text-sky-500 bg-sky-50' },
  insurance_prior_auth: { label: 'Prior Auth', icon: Shield, color: 'text-amber-500 bg-amber-50' },
  medication_related: { label: 'Medication', icon: Pill, color: 'text-pink-500 bg-pink-50' },
  administrative_form: { label: 'Admin Form', icon: FileText, color: 'text-slate-500 bg-slate-50' },
  miscellaneous: { label: 'Other', icon: FileText, color: 'text-slate-400 bg-slate-50' },
};

const SOURCE_ICONS = {
  fax: '📠', scan: '🖨️', lab_interface: '🧪', email: '📧', portal_upload: '🌐', manual_upload: '📎',
};

// ─── OCR Patient Extraction ──────────────────────────────────
function extractPatientFromOCR(ocrText) {
  if (!ocrText) return {};
  const text = ocrText;

  // Name: "Patient: First Last" or "Member: First Last"
  const nameMatch = text.match(/(?:patient|member|name)[:\s]+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)+)/i);
  let firstName = '', lastName = '';
  if (nameMatch?.[1]) {
    const parts = nameMatch[1].trim().split(/\s+/);
    firstName = parts[0] || '';
    lastName = parts.slice(-1)[0] || '';
    // Handle middle name: "Angela Marie Martinez" → first=Angela, last=Martinez
    if (parts.length >= 3) lastName = parts[parts.length - 1];
  }

  // DOB: "DOB: 01/18/1975" or "Date of Birth: 01/18/1975"
  const dobMatch = text.match(/(?:dob|date of birth|birth date|d\.o\.b)[.:\s]+(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/i);
  let dob = '';
  if (dobMatch?.[1]) {
    // Normalize to YYYY-MM-DD
    const parts = dobMatch[1].split(/[\/\-\.]/);
    if (parts.length === 3) {
      const [m, d, y] = parts;
      const year = y.length === 2 ? `20${y}` : y;
      dob = `${year}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
    }
  }

  // Sex: "Sex: Female" or "Gender: Male"
  const sexMatch = text.match(/(?:sex|gender)[.:\s]+(male|female|m\b|f\b)/i);
  let sex = '';
  if (sexMatch?.[1]) {
    sex = sexMatch[1].toLowerCase().startsWith('f') ? 'F' : 'M';
  }

  // Phone: "Phone: (806) 555-0248"
  const phoneMatch = text.match(/(?:phone|tel|cell)[.:\s]+([(\d\s\-\.)]{10,})/i);
  const phone = phoneMatch?.[1]?.trim().replace(/[^\d]/g, '').slice(0, 10) || '';

  return { firstName, lastName, dob, sex, phone };
}

export default function ClinicDocManager() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [showAccessPanel, setShowAccessPanel] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null); // doc being reviewed in full-screen overlay
  const [showNewPatient, setShowNewPatient] = useState(null); // { docId, docTitle, extracted }
  const fileInputRef = React.useRef(null);

  const fetchDashboard = useCallback(() => {
    api.get('/doc-manager/dashboard').then(r => setDashboard(r.data)).catch(e => {
      if (e.response?.status === 403) toast.error('No access to Clinic Document Manager');
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleSeed = async () => {
    setSeeding(true);
    try { await api.post('/doc-manager/seed-demo'); fetchDashboard(); toast.success('Demo documents loaded'); }
    catch { toast.error('Failed'); } finally { setSeeding(false); }
  };

  const handleBulkConfirmHigh = async () => {
    if (!dashboard) return;
    const ids = dashboard.high_confidence.items.map(d => d.id);
    if (ids.length === 0) return;
    setBulkSending(true);
    try {
      const res = await api.post('/doc-manager/bulk-confirm', { doc_ids: ids });
      toast.success(`${res.data.routed} documents routed to physicians`);
      fetchDashboard();
    } catch { toast.error('Bulk confirm failed'); }
    finally { setBulkSending(false); }
  };

  const handleImportPdf = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let totalProcessed = 0;
    let totalFailed = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source_type', 'manual_upload');
        const res = await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const data = res.data;
        if (data.source === 'zip') {
          totalProcessed += data.files_processed || 0;
          totalFailed += data.files_failed || 0;
          toast.success(`ZIP: ${data.files_processed} files extracted from ${file.name}`);
        } else if (data.multi_doc_candidate) {
          totalProcessed += 1;
          toast.info(`${file.name}: ${data.page_count} pages — may contain multiple documents`);
        } else {
          totalProcessed += data.files_processed || 1;
        }
      } catch (err) {
        totalFailed++;
        toast.error(`Failed: ${file.name} — ${err.response?.data?.detail || 'Upload error'}`);
      }
    }
    if (totalProcessed > 0) {
      toast.success(`${totalProcessed} document${totalProcessed > 1 ? 's' : ''} imported. AI processing started.`);
      setTimeout(fetchDashboard, 3000);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmSingle = async (doc) => {
    try {
      await api.post('/doc-manager/confirm-and-route', {
        doc_id: doc.id,
        patient_id: doc.suggested_patient_id || '',
        category: doc.suggested_category,
        provider_id: doc.suggested_provider_id || '',
        provider_name: doc.suggested_provider || '',
      });
      toast.success(`Routed: ${doc.title}`);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>;

  if (!dashboard) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Shield className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <p className="text-sm text-slate-500">Access denied or failed to load</p>
      </div>
    </div>
  );

  const { high_confidence, medium_confidence, low_confidence, total } = dashboard;

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="doc-manager-page">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b shadow-sm flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Clinic Document Manager</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">{total} documents awaiting triage</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Import button + drag zone */}
          <input type="file" ref={fileInputRef} className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.heic,.bmp,.gif,.webp,.docx,.doc,.txt,.rtf,.zip"
            multiple onChange={handleImportPdf} data-testid="pdf-file-input" />
          <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => fileInputRef.current?.click()}
            disabled={uploading} data-testid="import-pdf-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Importing...' : 'Import Documents'}
          </Button>
          {total === 0 && (
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} className="text-xs gap-1">
              {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Load Demo Documents
            </Button>
          )}
          {/* Settings gear — access control */}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
            onClick={() => setShowAccessPanel(!showAccessPanel)} data-testid="doc-manager-settings">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Button>
          {high_confidence.count > 0 && (
            <Button size="sm" onClick={handleBulkConfirmHigh} disabled={bulkSending}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-confirm-high">
              {bulkSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Confirm & Route All High ({high_confidence.count})
            </Button>
          )}
        </div>
      </div>

      {/* Access Control Panel */}
      {showAccessPanel && <AccessControlPanel onClose={() => setShowAccessPanel(false)} />}

      {/* New Patient Prompt */}
      {showNewPatient && (
        <NewPatientPrompt
          docTitle={showNewPatient.docTitle}
          extracted={showNewPatient.extracted || {}}
          onCreatePatient={async (patientData) => {
            try {
              const res = await api.post('/patients', patientData);
              const newPatient = res.data.patient || res.data;
              const pid = newPatient.id || newPatient._id;
              const name = `${patientData.first_name} ${patientData.last_name}`;
              await api.post('/doc-manager/update-classification', {
                doc_id: showNewPatient.docId, patient_id: pid, patient_name: name,
              });
              toast.success(`Created chart for ${name} and linked document`);
              setShowNewPatient(null);
              fetchDashboard();
            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create patient'); }
          }}
          onDismiss={() => {
            api.post('/doc-manager/update-classification', {
              doc_id: showNewPatient.docId, patient_name: 'Not a clinic patient',
            }).then(() => { toast.info('Document marked as non-patient'); fetchDashboard(); })
            .catch(() => {});
            setShowNewPatient(null);
          }}
          onClose={() => setShowNewPatient(null)}
        />
      )}

      {/* Full-Screen Document Review Overlay */}
      {reviewDoc && (
        <DocumentReviewOverlay
          doc={reviewDoc}
          onClose={() => setReviewDoc(null)}
          onConfirmed={() => { setReviewDoc(null); fetchDashboard(); }}
          onNewPatient={(docId, docTitle, extracted) => setShowNewPatient({ docId, docTitle, extracted })}
        />
      )}

      {/* Three-lane triage view */}
      <div className="flex-1 overflow-hidden flex gap-4 p-4">
        {/* HIGH CONFIDENCE — Green Lane */}
        <TriageLane
          title="High Confidence" subtitle="90-100% — Quick confirm"
          color="emerald" items={high_confidence.items} count={high_confidence.count}
          onReview={setReviewDoc} onConfirm={handleConfirmSingle} onRefresh={fetchDashboard}
        />

        {/* MEDIUM CONFIDENCE — Yellow Lane */}
        <TriageLane
          title="Needs Review" subtitle="60-89% — Verify details"
          color="amber" items={medium_confidence.items} count={medium_confidence.count}
          onReview={setReviewDoc} onConfirm={handleConfirmSingle} onRefresh={fetchDashboard}
        />

        {/* LOW CONFIDENCE — Red Lane */}
        <TriageLane
          title="Manual Triage" subtitle="<60% — Classify & match"
          color="red" items={low_confidence.items} count={low_confidence.count}
          onReview={setReviewDoc} onConfirm={handleConfirmSingle} onRefresh={fetchDashboard}
        />
      </div>
    </div>
  );
}


function TriageLane({ title, subtitle, color, items, count, onReview, onConfirm, onRefresh }) {
  const borderColor = color === 'emerald' ? 'border-emerald-200' : color === 'amber' ? 'border-amber-200' : 'border-red-200';
  const bgColor = color === 'emerald' ? 'bg-emerald-50/50' : color === 'amber' ? 'bg-amber-50/50' : 'bg-red-50/50';
  const headerColor = color === 'emerald' ? 'text-emerald-700' : color === 'amber' ? 'text-amber-700' : 'text-red-700';
  const dotColor = color === 'emerald' ? 'bg-emerald-400' : color === 'amber' ? 'bg-amber-400' : 'bg-red-400';
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleBulkVoid = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/doc-manager/bulk-void', {
        doc_ids: Array.from(selected), reason: 'duplicate'
      });
      toast.success(`Voided ${res.data.voided} documents`);
      setSelected(new Set());
      onRefresh();
    } catch { toast.error('Bulk void failed'); }
    finally { setBulkDeleting(false); }
  };

  return (
    <div className={`flex-1 rounded-2xl border ${borderColor} ${bgColor} flex flex-col overflow-hidden`}>
      <div className="px-4 py-3 border-b border-inherit flex items-center gap-2 shrink-0">
        <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
        <div>
          <h2 className={`text-sm font-bold ${headerColor}`}>{title}</h2>
          <p className="text-[10px] text-slate-400">{subtitle}</p>
        </div>
        <Badge variant="outline" className="text-[10px] ml-auto">{count}</Badge>
        {/* Select all for bulk delete */}
        {items.length > 0 && (
          <button onClick={() => setSelected(selected.size === items.length ? new Set() : new Set(items.map(d => d.id)))}
            className="text-[10px] text-slate-400 hover:text-slate-600 ml-1 shrink-0">
            {selected.size === items.length ? 'Deselect' : 'Select All'}
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 flex items-center justify-between shrink-0">
          <p className="text-[11px] text-rose-700 font-medium">{selected.size} selected</p>
          <button onClick={handleBulkVoid} disabled={bulkDeleting}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-medium disabled:opacity-60"
            data-testid="bulk-void-btn">
            {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            {bulkDeleting ? 'Voiding...' : `Void ${selected.size} Duplicates`}
          </button>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {items.length === 0 ? (
            <p className="text-[11px] text-slate-400 text-center py-8">No documents</p>
          ) : items.map(doc => (
            <DocTriageCard key={doc.id} doc={doc}
              onReview={() => onReview(doc)}
              onQuickConfirm={() => onConfirm(doc)}
              onDeleted={onRefresh}
              isSelected={selected.has(doc.id)}
              onToggleSelect={() => toggleSelect(doc.id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}


function DocTriageCard({ doc, onReview, onQuickConfirm, onDeleted, isSelected, onToggleSelect }) {
  const [confirming, setConfirming] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextPos, setContextPos] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const catCfg = CATEGORY_CONFIG[doc.suggested_category] || CATEGORY_CONFIG.miscellaneous;
  const CatIcon = catCfg.icon;
  const sourceIcon = SOURCE_ICONS[doc.source_type] || String.fromCodePoint(0x1F4C4);

  const handleQuickConfirm = async (e) => {
    e.stopPropagation();
    setConfirming(true);
    await onQuickConfirm();
    setConfirming(false);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  return (
    <>
      {showContextMenu && (
        <div className="fixed inset-0 z-50" onClick={() => setShowContextMenu(false)}>
          <div className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 py-1 w-52 z-50"
            style={{ top: contextPos.y, left: Math.min(contextPos.x, window.innerWidth - 220) }}
            onClick={e => e.stopPropagation()}>
            <div className="px-3 py-1.5 border-b border-slate-100">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider truncate">{doc.title}</p>
            </div>
            <button onClick={() => { setShowContextMenu(false); onReview(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" /> Open &amp; Review
            </button>
            <button onClick={() => { setShowContextMenu(false); handleQuickConfirm({ stopPropagation: () => {} }); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-600 hover:bg-emerald-50">
              <Check className="w-3.5 h-3.5" /> Quick Confirm &amp; Route
            </button>
            <div className="border-t border-slate-100 mt-1" />
            <button onClick={() => { setShowContextMenu(false); setShowDeleteModal(true); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50"
              data-testid={`ctx-delete-${doc.id}`}>
              <X className="w-3.5 h-3.5" /> Delete / Void Document
            </button>
            {doc.parent_doc_id && (
              <button onClick={() => {
                setShowContextMenu(false);
                api.post(`/doc-manager/undo-split/${doc.parent_doc_id}`).then(() => {
                  toast.success('Split undone — original restored to triage queue');
                  onDeleted?.();
                }).catch(() => toast.error('Undo failed'));
              }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-purple-600 hover:bg-purple-50"
              data-testid={`ctx-undo-split-${doc.id}`}>
                <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Undo Split (Restore Original)
              </button>
            )}
          </div>
        </div>
      )}

      <div className={`rounded-xl bg-white border shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group
          ${isSelected ? 'border-rose-300 ring-1 ring-rose-200' : 'border-slate-200'}`}
        onClick={onReview}
        onContextMenu={handleContextMenu}
        data-testid={`triage-card-${doc.id}`}>
        <div className="px-3 py-2.5 flex items-center gap-2.5">
          {/* Selection checkbox */}
          <div onClick={e => { e.stopPropagation(); onToggleSelect?.(); }}
            className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-colors
              ${isSelected ? 'bg-rose-500 border-rose-500' : 'border-slate-300 hover:border-slate-400'}`}>
            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
          </div>
          <span className="text-base shrink-0">{sourceIcon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{doc.title}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`text-[8px] h-4 px-1.5 ${catCfg.color}`}>
                <CatIcon className="w-2.5 h-2.5 mr-0.5" />{catCfg.label}
              </Badge>
              {doc.suggested_patient && (
                <span className="text-[10px] text-slate-500 truncate max-w-[80px]">{doc.suggested_patient}</span>
              )}
              {!doc.suggested_patient && (
                <span className="text-[10px] text-amber-500 font-medium">Unmatched</span>
              )}
              {doc.parent_doc_id && (
                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 font-medium shrink-0">split</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[9px] text-slate-300">{doc.confidence_score}%</span>
            <button onClick={handleQuickConfirm} disabled={confirming}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all"
              title="Quick confirm & route" data-testid={`quick-confirm-${doc.id}`}>
              {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
            <div className="p-1 rounded-lg bg-slate-50 group-hover:bg-blue-50 text-slate-400 group-hover:text-blue-500 transition-all"
              title="Right-click for more options">
              <Maximize2 className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
        {doc.ocr_preview && (
          <div className="px-3 pb-2">
            <p className="text-[10px] text-slate-400 truncate font-mono">{doc.ocr_preview?.slice(0, 80)}…</p>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteDocModal
          doc={doc}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => { setShowDeleteModal(false); onDeleted?.(); }}
        />
      )}
    </>
  );
}


/* ═══════════════════════════════════════
   DELETE / VOID DOCUMENT MODAL
   ═══════════════════════════════════════ */

const VOID_REASONS = [
  { value: 'duplicate',       label: 'Duplicate document',    desc: 'Already received and processed' },
  { value: 'scanned_in_error', label: 'Scanned in error',    desc: 'Wrong document imported' },
  { value: 'wrong_clinic',    label: 'Test / Wrong clinic',   desc: 'Not intended for this clinic' },
  { value: 'illegible',       label: 'Blank or illegible',    desc: 'Cannot be read or processed' },
  { value: 'other',           label: 'Other',                 desc: 'Add note below' },
];

function DeleteDocModal({ doc, onClose, onDeleted }) {
  const [reason, setReason] = useState('duplicate');
  const [note, setNote] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/doc-manager/documents/${doc.id}?reason=${reason}&note=${encodeURIComponent(note)}`);
      toast.success('Document voided — removed from queue');
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to void document');
    } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[440px] overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="delete-doc-modal">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" /> Void Document
            </h3>
            <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-xs">{doc.title}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {/* Medical-legal note */}
          <div className="px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
            <p className="text-[10px] text-blue-700">
              <strong>Note:</strong> The document will be removed from the triage queue.
              A lightweight intake record is kept (who voided it, when, and why) in case a sender
              inquires about a fax. It will not appear in any patient chart.
            </p>
          </div>

          <p className="text-xs font-semibold text-slate-600">Reason for voiding</p>
          <div className="space-y-1.5">
            {VOID_REASONS.map(r => (
              <label key={r.value}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all
                  ${reason === r.value ? 'border-rose-300 bg-rose-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="void_reason" value={r.value}
                  checked={reason === r.value} onChange={() => setReason(r.value)}
                  className="accent-rose-500" />
                <div>
                  <p className={`text-xs font-medium ${reason === r.value ? 'text-rose-700' : 'text-slate-700'}`}>
                    {r.label}
                  </p>
                  <p className="text-[10px] text-slate-400">{r.desc}</p>
                </div>
              </label>
            ))}
          </div>

          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Optional note</label>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Add context if needed..."
              className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-200" />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 justify-end">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-60"
            data-testid="confirm-void-btn">
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            {deleting ? 'Voiding...' : 'Void Document'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   FULL-SCREEN DOCUMENT REVIEW OVERLAY
   ═══════════════════════════════════════ */

/* Reusable card with confirm/dismiss for the AI results action queue */
function ActionCard({ title, confirmed, color, onConfirm, onDismiss, children }) {
  const colors = {
    emerald: { bg: confirmed ? 'bg-emerald-600' : 'bg-emerald-50', border: 'border-emerald-200', text: confirmed ? 'text-white' : 'text-emerald-800', label: confirmed ? 'text-emerald-100' : 'text-emerald-700', btn: 'bg-white text-emerald-700 hover:bg-emerald-50' },
    blue:    { bg: confirmed ? 'bg-blue-600'    : 'bg-blue-50',    border: 'border-blue-200',    text: confirmed ? 'text-white' : 'text-blue-800',    label: confirmed ? 'text-blue-100'    : 'text-blue-700',    btn: 'bg-white text-blue-700 hover:bg-blue-50' },
    purple:  { bg: confirmed ? 'bg-purple-600'  : 'bg-purple-50',  border: 'border-purple-200',  text: confirmed ? 'text-white' : 'text-purple-800',  label: confirmed ? 'text-purple-100'  : 'text-purple-700',  btn: 'bg-white text-purple-700 hover:bg-purple-50' },
  };
  const c = colors[color] || colors.emerald;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-3 transition-all`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${c.label}`}>
          {confirmed ? '✓ ' : ''}{title}
        </p>
        {confirmed && (
          <button onClick={onDismiss} className="text-[9px] opacity-70 hover:opacity-100 text-white">
            Undo
          </button>
        )}
      </div>
      <div className={c.text}>{children}</div>
      {!confirmed && (
        <button onClick={onConfirm}
          className={`mt-2 w-full py-1.5 rounded-lg text-[11px] font-medium ${c.btn} border ${c.border}`}>
          ✓ Confirm
        </button>
      )}
    </div>
  );
}

function DocumentReviewOverlay({ doc, onClose, onConfirmed, onNewPatient }) {
  const [editCategory, setEditCategory] = useState(doc.suggested_category || 'miscellaneous');
  const [editProvider, setEditProvider] = useState(doc.suggested_provider || '');
  const [editProviderId, setEditProviderId] = useState(doc.suggested_provider_id || '');
  const [editPatient, setEditPatient] = useState(doc.suggested_patient || '');
  const [editPatientId, setEditPatientId] = useState(doc.suggested_patient_id || '');
  const [providerSearch, setProviderSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [providers, setProviders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [showPatientPicker, setShowPatientPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  const catCfg = CATEGORY_CONFIG[editCategory] || CATEGORY_CONFIG.miscellaneous;
  const CatIcon = catCfg.icon;
  const extracted = extractPatientFromOCR(doc.ocr_text || doc.ocr_preview || '');
  const patientNotMatched = !editPatientId;
  const isMultiPage = (doc.page_count || 1) > 1;

  // ── Readiness — all 3 fields must be filled before routing ──────────
  const categoryReady = !!(editCategory && editCategory !== 'miscellaneous');
  const providerReady = !!(editProvider && editProviderId);
  const patientReady  = !!(editPatient && editPatientId);
  const allReady = categoryReady && providerReady && patientReady;
  const fieldWrap = (ready) => ready
    ? 'rounded-xl border border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200'
    : 'rounded-xl border border-slate-200 bg-white';
  const [aiMultiDoc, setAiMultiDoc] = useState(false);
  const [keptAsOne, setKeptAsOne] = useState(false); // user dismissed the split suggestion
  const [splitting, setSplitting] = useState(false);
  const [commandText, setCommandText] = useState('');
  const [commandLoading, setCommandLoading] = useState(false);
  const [commandResult, setCommandResult] = useState(null);

  // ── Action queue ─────────────────────────────────────────────
  const [confirmedItems, setConfirmedItems] = useState({});
  const [processing, setProcessing] = useState(false);
  const [processedResult, setProcessedResult] = useState(null);
  const [providersList, setProvidersList] = useState([]);
  const [providerPickerSearch, setProviderPickerSearch] = useState('');
  const [resplitHint, setResplitHint] = useState('');
  const [resplitting, setResplitting] = useState(false);
  const [showManualSplit, setShowManualSplit] = useState(false);

  const confirmCount = Object.keys(confirmedItems).filter(k => confirmedItems[k]).length;

  const processAllActions = async () => {
    if (confirmCount === 0) return;
    setProcessing(true);
    try {
      const patientId   = confirmedItems.patient?.id   || editPatientId  || '';
      const patientName = confirmedItems.patient?.name || editPatient     || '';
      const providerId  = confirmedItems.provider?.id  || editProviderId  || '';
      const providerName = confirmedItems.provider?.name || editProvider  || '';
      const category    = confirmedItems.category      || editCategory;

      const docsToRoute = (aiResults?.was_split && aiResults?.child_docs?.length > 0)
        ? aiResults.child_docs : [{ id: doc.id }];

      if (patientId || category || providerId) {
        await api.post('/doc-manager/update-classification', {
          doc_id: doc.id, category, patient_id: patientId, patient_name: patientName,
          provider_id: providerId, provider_name: providerName,
        });
      }

      let routed = 0;
      const errors = [];
      for (const d of docsToRoute) {
        try {
          const docCategory = d.suggested_category || category;
          await api.post('/doc-manager/confirm-and-route', {
            doc_id: d.id, patient_id: patientId, category: docCategory,
            provider_id: providerId, provider_name: providerName,
          });
          routed++;
        } catch (e) {
          errors.push(d.title || d.id);
        }
      }
      if (errors.length > 0) {
        toast.warning(`${routed} routed, ${errors.length} failed: ${errors.slice(0,2).join(', ')}`);
      }

      // Show done screen with destination info for each doc
      const destInfo = docsToRoute.map(d => ({
        id: d.id,
        title: d.title || 'Document',
        category: d.suggested_category || category,
        bucket: ['lab_result','imaging_report','lab','imaging'].includes(d.suggested_category || category)
          ? 'Lab/Xray bucket' : 'Correspondence inbox',
      }));
      setProcessedResult({
        docCount: routed,
        providerName,
        patientName,
        docs: destInfo,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Processing failed');
    } finally { setProcessing(false); }
  };

  const handleResplit = async () => {
    setResplitting(true);
    try {
      // Use the AI command endpoint with a hint about missing documents
      const hint = resplitHint.trim()
        ? `Split this PDF. Hint: ${resplitHint}`
        : 'Split this PDF into all separate documents including any missed sections';
      const res = await api.post(`/doc-manager/ai-command/${doc.id}`, { command: hint });
      // Also run a hard re-split
      const splitRes = await api.post(`/doc-manager/split/${doc.id}`);
      if (splitRes.data.split) {
        // Refresh aiResults with new split data
        const reprocessRes = await api.post(`/doc-manager/reprocess/${doc.id}`);
        setAiResults(reprocessRes.data);
        setConfirmedItems({});
        toast.success(`Re-analyzed — found ${reprocessRes.data.split_count || splitRes.data.documents_created} documents`);
      } else {
        toast.info('Re-analysis complete — same split detected. Try adding more detail in the hint.');
      }
    } catch (e) {
      toast.error('Re-split failed');
    } finally { setResplitting(false); }
  };

  const handleAiCommand = async () => {
    if (!commandText.trim()) return;
    setCommandLoading(true);
    try {
      const res = await api.post(`/doc-manager/ai-command/${doc.id}`, { command: commandText });
      setCommandResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Command failed');
    } finally { setCommandLoading(false); }
  };

  const executeCommandAction = async (action) => {
    const p = action.params || {};
    if (action.type === 'assign_patient' && p.patient_id) {
      setEditPatient(p.patient_name || '');
      setEditPatientId(p.patient_id);
      toast.success(`Patient set: ${p.patient_name}`);
    }
    if (action.type === 'assign_provider' && p.provider_id) {
      setEditProvider(p.provider_name || '');
      setEditProviderId(p.provider_id);
      toast.success(`Provider set: ${p.provider_name}`);
    }
    if (action.type === 'set_category' && p.category) {
      setEditCategory(p.category);
      toast.success(`Category set: ${p.category}`);
    }
    if (action.type === 'split_by_date' || action.type === 'split_by_section') {
      setCommandResult(null);
      setCommandText('');
      handleSplit();
    }
  };
  const [reprocessing, setReprocessing] = useState(false);
  const [aiResults, setAiResults] = useState(null); // shows results dialog after re-run
  const [localNewPatient, setLocalNewPatient] = useState(null); // {extracted, context:'ai'|'picker'}
  const ocrMissing = !doc.ocr_text && !doc.ocr_preview;
  const ocrFailed = doc.ocr_status === 'failed';

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const [res, provRes] = await Promise.all([
        api.post(`/doc-manager/reprocess/${doc.id}`),
        api.get('/providers'),
      ]);
      if (res.data.has_multi_doc_signals || res.data.page_count > 1) setAiMultiDoc(true);
      setProvidersList(provRes.data.providers || []);
      setConfirmedItems({}); // reset action queue for fresh run
      setAiResults(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI analysis failed — check that the file is accessible on disk');
    } finally { setReprocessing(false); }
  };

  // ── Local patient creation — keeps you inside the overlay ──────────
  const handleLocalCreatePatient = async (patientData) => {
    try {
      const res = await api.post('/patients', patientData);
      const newPatient = res.data.patient || res.data;
      const pid = newPatient.id || newPatient._id;
      const name = `${patientData.first_name} ${patientData.last_name}`.trim();
      // Link document to new patient
      await api.post('/doc-manager/update-classification', {
        doc_id: doc.id, patient_id: pid, patient_name: name,
      });
      toast.success(`Chart created for ${name} — continuing workflow`);
      // Update overlay patient state immediately
      setEditPatient(name);
      setEditPatientId(pid);
      // If AI Results panel is open, inject the new patient without closing it
      if (aiResults) {
        setAiResults(prev => prev ? {
          ...prev,
          suggested_patient: name,
          suggested_patient_id: pid,
          confidence_score: 95,
        } : prev);
        setConfirmedItems(prev => ({ ...prev, patient: { id: pid, name } }));
      }
      setLocalNewPatient(null); // close only the patient form, stay on overlay
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create patient');
    }
  };

  const handleSplit = async () => {
    setSplitting(true);
    try {
      const res = await api.post(`/doc-manager/split/${doc.id}`);
      if (res.data.split) {
        toast.success(`Split into ${res.data.documents_created} separate documents`);
        onConfirmed();
      } else {
        toast.info(res.data.message || 'AI did not detect multiple documents');
        setSplitting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Split failed');
      setSplitting(false);
    }
  };

  // Load PDF on mount
  useEffect(() => {
    const loadPdf = async () => {
      try {
        const token = localStorage.getItem('prattos_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/pdf') || ct.includes('image/')) {
            const blob = await res.blob();
            setPdfUrl(URL.createObjectURL(blob));
          }
        }
      } catch {}
      finally { setPdfLoading(false); }
    };
    loadPdf();
    loadProviders('');
  }, [doc.id]);

  const loadProviders = async (search) => {
    try {
      const res = await api.get('/providers');
      let list = res.data.providers || [];
      if (search) list = list.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));
      setProviders(list);
    } catch { setProviders([]); }
  };

  const loadPatients = async (search) => {
    try {
      const res = await api.get(`/patients?search=${encodeURIComponent(search || '')}`);
      setPatients((res.data.patients || []).slice(0, 10));
    } catch { setPatients([]); }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await api.post('/doc-manager/update-classification', {
        doc_id: doc.id, category: editCategory,
        provider_name: editProvider, provider_id: editProviderId,
        patient_id: editPatientId, patient_name: editPatient,
      });
      await api.post('/doc-manager/confirm-and-route', {
        doc_id: doc.id,
        patient_id: editPatientId || '',
        category: editCategory,
        provider_name: editProvider || '',
        provider_id: editProviderId || '',
      });

      // If this document was split, also route all its children so they leave the triage queue
      if (doc.split_into?.length > 0) {
        let childRouted = 0;
        for (const childId of doc.split_into) {
          try {
            await api.post('/doc-manager/confirm-and-route', {
              doc_id: childId,
              patient_id: editPatientId || '',
              category: '',   // children use their own stored category
              provider_name: editProvider || '',
              provider_id: editProviderId || '',
            });
            childRouted++;
          } catch {}
        }
        if (childRouted > 0) {
          toast.success(`Document confirmed & ${childRouted} split sections routed to provider`);
        } else {
          toast.success('Document confirmed & routed to provider');
        }
      } else {
        toast.success('Document confirmed & routed to provider');
      }

      onConfirmed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex bg-black/60 backdrop-blur-sm" data-testid="doc-review-overlay">
      {/* LEFT PANEL — classification controls */}
      <div className="w-80 shrink-0 bg-white flex flex-col h-full border-r border-slate-200 shadow-xl">
        {/* Header */}
        <div className="px-4 py-3.5 border-b border-slate-100 flex items-center gap-2">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">{doc.title}</p>
            <p className="text-[10px] text-slate-400">{doc.confidence_score}% confidence · Manual Triage</p>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-4 py-4 space-y-4">

            {/* AI Command Bar */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
              <div className="px-3 py-2 flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    value={commandText}
                    onChange={e => setCommandText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAiCommand()}
                    placeholder='Ask AI: "split by lab date" · "file to Dr. Smith" · "divide into 3 notes"'
                    className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 placeholder:text-slate-300"
                    data-testid="doc-ai-command-input"
                  />
                </div>
                <button onClick={handleAiCommand} disabled={commandLoading || !commandText.trim()}
                  className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-40 transition-opacity"
                  data-testid="doc-ai-command-submit">
                  {commandLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Go'}
                </button>
              </div>
              <div className="px-3 pb-2 flex gap-1.5 flex-wrap">
                {['split by date', 'divide into sections', 'assign to Dr. Smith'].map(ex => (
                  <button key={ex} onClick={() => setCommandText(ex)}
                    className="text-[9px] px-2 py-0.5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-500 transition-colors">
                    {ex}
                  </button>
                ))}
              </div>

              {/* Command Result Preview */}
              {commandResult && (
                <div className="border-t border-slate-200 px-3 py-2.5 bg-white">
                  <p className="text-[10px] text-slate-500 mb-1.5 italic">"{commandResult.understood}"</p>
                  <div className="space-y-1.5">
                    {(commandResult.actions || []).map((action, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 py-1.5 px-2.5 rounded-lg bg-blue-50 border border-blue-100">
                        <div>
                          <p className="text-[11px] font-medium text-blue-800">{action.description}</p>
                          <p className="text-[9px] text-blue-500 capitalize">{action.type.replace(/_/g,' ')}</p>
                        </div>
                        <button onClick={() => executeCommandAction(action)}
                          className="shrink-0 px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-medium"
                          data-testid={`execute-action-${i}`}>
                          Apply
                        </button>
                      </div>
                    ))}
                    {commandResult.actions?.length === 0 && (
                      <p className="text-[11px] text-slate-500">No actions found — try rephrasing</p>
                    )}
                  </div>
                  <button onClick={() => { setCommandResult(null); setCommandText(''); }}
                    className="mt-1.5 text-[10px] text-slate-400 hover:text-slate-600">
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* OCR status banner */}
            {(ocrMissing || ocrFailed) && (
              <div className="rounded-xl bg-slate-100 border border-slate-200 p-3">
                <p className="text-[11px] font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  {ocrFailed ? 'AI Analysis Failed' : 'Not Yet Analyzed'}
                </p>
                <p className="text-[10px] text-slate-500 mb-2">
                  {ocrFailed
                    ? 'The AI could not extract text from this document. Click below to retry.'
                    : 'This document has not been analyzed by AI yet. Run analysis to extract patient, category, and enable splitting.'}
                </p>
                <button onClick={handleReprocess} disabled={reprocessing}
                  className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-medium transition-colors disabled:opacity-60"
                  data-testid="overlay-reprocess-btn">
                  {reprocessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                  {reprocessing ? 'Analyzing...' : 'Run AI Analysis'}
                </button>
              </div>
            )}

            {/* Patient not matched banner + extracted info */}
            {patientNotMatched && (extracted.firstName || extracted.lastName) && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-[10px] font-semibold text-amber-700 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Patient Not in System
                </p>
                <p className="text-[11px] text-amber-800 font-medium">
                  {[extracted.firstName, extracted.lastName].filter(Boolean).join(' ')}
                </p>
                {extracted.dob && <p className="text-[10px] text-amber-700">DOB: {extracted.dob}</p>}
                {extracted.sex && <p className="text-[10px] text-amber-700">Sex: {extracted.sex}</p>}
                <button
                  onClick={() => setLocalNewPatient({ extracted })}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-medium transition-colors"
                  data-testid="overlay-add-patient-btn">
                  <UserPlus className="w-3.5 h-3.5" /> Add Patient to System
                </button>
              </div>
            )}

            {/* Category */}
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">Document Type</label>
                {categoryReady && <span className="text-[9px] text-emerald-600 font-semibold flex items-center gap-0.5"><Check className="w-3 h-3" /> Set</span>}
              </div>
              <button onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                className={`w-full text-left px-3 py-2 ${fieldWrap(categoryReady)} text-xs font-medium text-slate-700 hover:border-slate-300 flex items-center gap-2`}
                data-testid="overlay-category-picker">
                <CatIcon className={`w-3.5 h-3.5 ${categoryReady ? 'text-emerald-500' : 'text-slate-500'}`} />
                {catCfg.label}
                <ChevronDown className="w-3.5 h-3.5 ml-auto text-slate-400" />
              </button>
              {showCategoryPicker && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-xl z-20 py-1 max-h-52 overflow-y-auto">
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button key={key} onClick={() => { setEditCategory(key); setShowCategoryPicker(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2.5 ${editCategory === key ? 'bg-blue-50 text-blue-700' : 'text-slate-600'}`}>
                        <Icon className="w-3.5 h-3.5" />{cfg.label}
                        {editCategory === key && <Check className="w-3.5 h-3.5 ml-auto text-blue-500" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Provider */}
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">Assign to Provider</label>
                {providerReady && <span className="text-[9px] text-emerald-600 font-semibold flex items-center gap-0.5"><Check className="w-3 h-3" /> Set</span>}
              </div>
              <button onClick={() => { setShowProviderPicker(!showProviderPicker); loadProviders(''); }}
                className={`w-full text-left px-3 py-2 ${fieldWrap(providerReady)} text-xs font-medium text-slate-700 hover:border-slate-300 flex items-center gap-2`}
                data-testid="overlay-provider-picker">
                <User className={`w-3.5 h-3.5 ${providerReady ? 'text-emerald-500' : 'text-slate-400'}`} />
                <span className={editProvider ? 'text-slate-700' : 'text-slate-400'}>{editProvider || 'Select provider...'}</span>
                <ChevronDown className="w-3.5 h-3.5 ml-auto text-slate-400" />
              </button>
              {showProviderPicker && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-xl z-20">
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input value={providerSearch} onChange={e => { setProviderSearch(e.target.value); loadProviders(e.target.value); }}
                      placeholder="Search providers..." className="flex-1 text-xs focus:outline-none"
                      autoFocus />
                  </div>
                  <div className="max-h-40 overflow-y-auto py-1">
                    {providers.map(p => (
                      <button key={p.id || p.name} onClick={() => { setEditProvider(p.name); setEditProviderId(p.id || ''); setShowProviderPicker(false); setProviderSearch(''); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 ${editProvider === p.name ? 'bg-blue-50 text-blue-700' : 'text-slate-600'}`}>
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-bold text-slate-500 shrink-0">
                          {p.name?.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                        <div>
                          <p className="font-medium">{p.name}</p>
                          {p.specialty && <p className="text-[9px] text-slate-400">{p.specialty}</p>}
                        </div>
                        {editProvider === p.name && <Check className="w-3.5 h-3.5 ml-auto text-blue-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Patient */}
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">Patient</label>
                {patientReady && <span className="text-[9px] text-emerald-600 font-semibold flex items-center gap-0.5"><Check className="w-3 h-3" /> Set</span>}
              </div>
              <button onClick={() => { setShowPatientPicker(!showPatientPicker); loadPatients(''); }}
                className={`w-full text-left px-3 py-2 ${patientReady ? fieldWrap(true) : patientNotMatched ? 'rounded-xl border border-amber-300 bg-amber-50 text-amber-700' : 'rounded-xl border border-slate-200 bg-white text-slate-700'} text-xs font-medium flex items-center gap-2 hover:border-slate-300`}
                data-testid="overlay-patient-picker">
                <User className={`w-3.5 h-3.5 ${patientReady ? 'text-emerald-500' : ''}`} />
                {editPatient || <span className="text-amber-600">Unmatched patient</span>}
                <ChevronDown className="w-3.5 h-3.5 ml-auto text-slate-400" />
              </button>
              {showPatientPicker && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-xl z-20">
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input value={patientSearch} onChange={e => { setPatientSearch(e.target.value); loadPatients(e.target.value); }}
                      placeholder="Search patients..." className="flex-1 text-xs focus:outline-none"
                      autoFocus />
                  </div>
                  <div className="max-h-44 overflow-y-auto py-1">
                    {patients.map(p => {
                      const name = `${p.name?.first || ''} ${p.name?.last || ''}`.trim();
                      const pid = p.id || p._id;
                      return (
                        <button key={pid} onClick={() => { setEditPatient(name); setEditPatientId(pid); setShowPatientPicker(false); setPatientSearch(''); }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center justify-between ${editPatientId === pid ? 'bg-blue-50 text-blue-700' : 'text-slate-600'}`}>
                          <div>
                            <p className="font-medium">{name}</p>
                            <p className="text-[9px] text-slate-400">DOB: {p.dob || '—'} · MRN: {p.mrn || '—'}</p>
                          </div>
                          {editPatientId === pid && <Check className="w-3.5 h-3.5 text-blue-500" />}
                        </button>
                      );
                    })}
                    {patients.length === 0 && patientSearch && (
                      <p className="text-[11px] text-slate-400 text-center py-3">No matches</p>
                    )}
                  </div>
                  <div className="border-t border-slate-100 p-2">
                    <button onClick={() => { setShowPatientPicker(false); setLocalNewPatient({ extracted }); }}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-medium transition-colors"
                      data-testid="overlay-new-patient-btn">
                      <UserPlus className="w-3.5 h-3.5" /> Create New Patient Chart
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* OCR text preview */}
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">Document Text</label>
              <div className="mt-1 rounded-xl bg-slate-50 border border-slate-200 p-3 max-h-48 overflow-y-auto">
                <p className="text-[10px] text-slate-600 whitespace-pre-wrap leading-relaxed font-mono">
                  {doc.ocr_text || doc.ocr_preview || 'No text extracted'}
                </p>
              </div>
            </div>

          </div>
        </ScrollArea>

        {/* AI Results Dialog — action-tracking panel */}
        {aiResults && (
          <div className="absolute inset-0 bg-white z-10 flex flex-col" data-testid="ai-results-panel">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div>
                <p className="text-sm font-bold text-slate-800">AI Analysis Results</p>
                <p className="text-[10px] text-slate-400">Check each item, then Process</p>
              </div>
              <button onClick={() => setAiResults(null)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-4 py-3 space-y-2.5">

                {/* 1. Document Type — only relevant if NOT splitting */}
                {!aiResults.was_split && (
                <ActionCard
                  title="Document Type"
                  confirmed={!!confirmedItems.category}
                  color="emerald"
                  onConfirm={() => {
                    setEditCategory(aiResults.suggested_category || editCategory);
                    setConfirmedItems(p => ({ ...p, category: aiResults.suggested_category || editCategory }));
                  }}
                  onDismiss={() => setConfirmedItems(p => { const n = {...p}; delete n.category; return n; })}>
                  <p className="text-sm font-bold text-emerald-800">
                    {CATEGORY_CONFIG[aiResults.suggested_category]?.label || 'Unknown'}
                  </p>
                  <p className="text-[10px] text-emerald-600">Confidence: {aiResults.confidence_score || 0}%</p>
                </ActionCard>
                )}
                {aiResults.was_split && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                    <p className="text-[10px] text-slate-500">
                      📋 Document type is set automatically for each split section below.
                    </p>
                  </div>
                )}

                {/* 2. Patient — three states: mismatch warning / matched / not in system */}
                {(() => {
                  const ext = aiResults.extracted || extracted;
                  const extFirst = (ext.first_name || ext.firstName || '').toLowerCase().trim();
                  const extLast  = (ext.last_name  || ext.lastName  || '').toLowerCase().trim();
                  const extName  = ext.full_name || [ext.first_name||ext.firstName, ext.last_name||ext.lastName].filter(Boolean).join(' ');
                  const sugFirst = (aiResults.suggested_patient || '').split(' ')[0]?.toLowerCase().trim();

                  // MISMATCH: AI matched someone but first name differs from what's in the doc
                  const isMismatch = aiResults.suggested_patient && extFirst && extFirst !== sugFirst;

                  if (isMismatch) {
                    return (
                      <div className="rounded-xl bg-rose-50 border border-rose-300 p-3">
                        <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" /> Name Mismatch — Different Patient?
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="rounded-lg bg-white border border-rose-200 p-2">
                            <p className="text-[9px] text-rose-500 uppercase font-semibold mb-0.5">Found in Document</p>
                            <p className="text-xs font-bold text-slate-800">{extName}</p>
                            {ext.dob && <p className="text-[10px] text-slate-500">DOB: {ext.dob}</p>}
                          </div>
                          <div className="rounded-lg bg-white border border-slate-200 p-2 opacity-70">
                            <p className="text-[9px] text-slate-400 uppercase font-semibold mb-0.5">Closest DB Match</p>
                            <p className="text-xs font-bold text-slate-600">{aiResults.suggested_patient}</p>
                            <p className="text-[10px] text-slate-400">{aiResults.confidence_score || 0}% confidence</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-rose-600 mb-2">
                          These appear to be <strong>different people</strong>. Add {extName.split(' ')[0] || 'this patient'} as a new patient, or search your records manually.
                        </p>
                        <div className="flex flex-col gap-1.5">
                          <button onClick={() => {
                            setLocalNewPatient({ extracted: {
                              firstName: ext.first_name||ext.firstName||'',
                              lastName: ext.last_name||ext.lastName||'',
                              dob: ext.dob||'', sex: ext.sex||'', phone: ext.phone||'',
                            }});
                          }} className="w-full py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-medium flex items-center justify-center gap-1.5"
                          data-testid="mismatch-add-patient-btn">
                            <UserPlus className="w-3 h-3" /> Add {extName || 'Patient'} to System
                          </button>
                          <div className="flex gap-1.5">
                            <button onClick={() => {
                              setEditPatient(aiResults.suggested_patient);
                              setEditPatientId(aiResults.suggested_patient_id || '');
                              setConfirmedItems(p => ({ ...p, patient: { id: aiResults.suggested_patient_id, name: aiResults.suggested_patient } }));
                            }} className="flex-1 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-[11px] font-medium">
                              Use {aiResults.suggested_patient?.split(' ')[0]} Anyway
                            </button>
                            <button onClick={() => setAiResults(null)}
                              className="flex-1 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-medium">
                              Search Manually
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (aiResults.suggested_patient) {
                    return (
                      <ActionCard
                        title="Patient Matched in System"
                        confirmed={!!confirmedItems.patient}
                        color="blue"
                        onConfirm={() => {
                          setEditPatient(aiResults.suggested_patient);
                          setEditPatientId(aiResults.suggested_patient_id || '');
                          setConfirmedItems(p => ({ ...p, patient: { id: aiResults.suggested_patient_id, name: aiResults.suggested_patient } }));
                        }}
                        onDismiss={() => setConfirmedItems(p => { const n = {...p}; delete n.patient; return n; })}>
                        <p className="text-sm font-bold text-blue-800">{aiResults.suggested_patient}</p>
                        <p className="text-[10px] text-blue-600">{aiResults.confidence_score || 0}% confidence</p>
                      </ActionCard>
                    );
                  }

                  // Not in system
                  return (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Patient Not in System</p>
                      {extName ? (
                        <>
                          <p className="text-sm font-bold text-amber-800">{extName}</p>
                          {ext.dob && <p className="text-[10px] text-amber-700">DOB: {ext.dob}</p>}
                          {ext.sex && <p className="text-[10px] text-amber-700">Sex: {ext.sex === 'F' ? 'Female' : 'Male'}</p>}
                        </>
                      ) : <p className="text-xs text-amber-700">No patient name found — use Search Manually</p>}
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => {
                          setLocalNewPatient({ extracted: {
                            firstName: ext.first_name||ext.firstName||'', lastName: ext.last_name||ext.lastName||'',
                            dob: ext.dob||'', sex: ext.sex||'', phone: ext.phone||'',
                          }});
                        }} className="flex-1 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-medium">
                          <UserPlus className="w-3 h-3 inline mr-1" /> Add to System
                        </button>
                        <button onClick={() => setAiResults(null)}
                          className="flex-1 py-1.5 rounded-lg border border-amber-200 text-amber-700 text-[11px] font-medium">
                          Search Manually
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* 3. Split — editable child docs + re-split hint */}
                {aiResults.was_split && aiResults.child_docs?.length > 0 && (
                  <div className={`rounded-xl border p-3 transition-all ${confirmedItems.split ? 'bg-purple-600 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-[10px] font-semibold uppercase tracking-wider ${confirmedItems.split ? 'text-white' : 'text-purple-700'}`}>
                        {confirmedItems.split ? '✓ ' : ''}Split into {aiResults.child_docs.length} Documents
                      </p>
                      {confirmedItems.split && (
                        <button onClick={() => setConfirmedItems(p => { const n={...p}; delete n.split; return n; })}
                          className="text-[9px] text-purple-100 hover:text-white">Undo</button>
                      )}
                    </div>

                    {/* List of detected docs */}
                    <div className="space-y-1 mb-2">
                      {aiResults.child_docs.map((cd, i) => {
                        const cfg = CATEGORY_CONFIG[cd.suggested_category] || CATEGORY_CONFIG.miscellaneous;
                        const Icon = cfg.icon;
                        return (
                          <div key={cd.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${confirmedItems.split ? 'bg-purple-500/40' : 'bg-white border border-purple-100'}`}>
                            <Icon className="w-3 h-3 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className={`text-[10px] font-medium truncate ${confirmedItems.split ? 'text-white' : 'text-slate-700'}`}>{cd.title}</p>
                              <p className={`text-[9px] ${confirmedItems.split ? 'text-purple-200' : 'text-slate-400'}`}>{cfg.label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {!confirmedItems.split && (
                      <>
                        {/* Missing a document? Re-split hint */}
                        <div className="border-t border-purple-200 pt-2 mt-2">
                          <p className="text-[10px] text-purple-600 font-medium mb-1">AI missed a document? Add a hint:</p>
                          <div className="flex gap-1.5">
                            <input
                              value={resplitHint}
                              onChange={e => setResplitHint(e.target.value)}
                              placeholder='e.g. "3 docs: lab, consult, imaging"'
                              className="flex-1 text-[10px] px-2 py-1.5 rounded-lg border border-purple-200 bg-white focus:outline-none focus:ring-1 focus:ring-purple-300"
                              data-testid="resplit-hint-input"
                            />
                            <button onClick={handleResplit} disabled={resplitting}
                              className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-medium disabled:opacity-60 whitespace-nowrap"
                              data-testid="resplit-btn">
                              {resplitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Re-split'}
                            </button>
                          </div>
                          <p className="text-[9px] text-purple-500 mt-1">Or use the AI command bar above for complex instructions</p>
                        </div>
                        <button onClick={() => setConfirmedItems(p => ({ ...p, split: true }))}
                          className="mt-2 w-full py-1.5 rounded-lg border border-purple-300 text-purple-700 text-[11px] font-medium hover:bg-purple-100">
                          ✓ Confirm Split
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* 4. Split needed — hidden if doc is already a child, or user chose Keep as One */}
                {!doc.parent_doc_id && !keptAsOne &&
                  (aiResults.has_multi_doc_signals || aiResults.page_count > 1) && !aiResults.was_split && (
                  <div className="rounded-xl bg-purple-50 border border-purple-200 p-3">
                    <p className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-1">Multiple Documents Detected</p>
                    <p className="text-[11px] text-purple-700 mb-2">
                      {aiResults.page_count > 1 ? `${aiResults.page_count}-page PDF` : 'Multi-document file'} — split into separate records
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => { setAiResults(null); handleSplit(); }}
                        className="flex-1 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-[11px] font-medium"
                        data-testid="results-split-btn">
                        Split Now
                      </button>
                      <button onClick={() => { setConfirmedItems(p => ({...p, split: false})); setKeptAsOne(true); }}
                        className="flex-1 py-1.5 rounded-lg border border-purple-200 text-purple-700 text-[11px] font-medium hover:bg-purple-50">
                        Keep as One
                      </button>
                    </div>
                  </div>
                )}

                {/* 5. Assign Provider */}
                <div className={`rounded-xl border p-3 ${confirmedItems.provider ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-[10px] font-semibold uppercase tracking-wider ${confirmedItems.provider ? 'text-white' : 'text-slate-600'}`}>
                      Assign to Provider
                    </p>
                    {confirmedItems.provider && (
                      <button onClick={() => setConfirmedItems(p => { const n={...p}; delete n.provider; return n; })}
                        className="text-[9px] text-slate-400 hover:text-white">
                        Change
                      </button>
                    )}
                  </div>
                  {confirmedItems.provider ? (
                    <p className="text-sm font-bold text-white">{confirmedItems.provider.name}</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Search className="w-3 h-3 text-slate-400 shrink-0" />
                        <input
                          value={providerPickerSearch}
                          onChange={e => setProviderPickerSearch(e.target.value)}
                          placeholder="Search providers..."
                          className="flex-1 text-xs bg-transparent focus:outline-none text-slate-600 placeholder:text-slate-300"
                          data-testid="results-provider-search"
                        />
                      </div>
                      <div className="space-y-1 max-h-28 overflow-y-auto">
                        {(providersList.length ? providersList : []).filter(p => {
                          const name = typeof p.name === 'string' ? p.name : '';
                          return !providerPickerSearch || name.toLowerCase().includes(providerPickerSearch.toLowerCase());
                        }).map(p => {
                          const name = typeof p.name === 'string' ? p.name : '';
                          return (
                            <button key={p.id} onClick={() => {
                              setEditProvider(name); setEditProviderId(p.id || '');
                              setConfirmedItems(prev => ({ ...prev, provider: { id: p.id, name } }));
                            }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 text-left text-[11px] text-slate-700 font-medium">
                              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-500 shrink-0">
                                {name.split(' ').map(n => n[0]).join('').slice(0,2)}
                              </div>
                              {name}
                            </button>
                          );
                        })}
                        {!providersList.length && (
                          <p className="text-[10px] text-slate-400 text-center py-2">No providers — re-run analysis first</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

              </div>
            </ScrollArea>

            {/* Done screen — shown after successful processing */}
            {processedResult && (
              <div className="absolute inset-0 bg-white z-20 flex flex-col items-center justify-center px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-base font-bold text-slate-800 mb-1">Done!</p>
                <p className="text-sm text-slate-600 mb-1">
                  {processedResult.docCount} document{processedResult.docCount > 1 ? 's' : ''} routed
                  {processedResult.providerName ? ` to ${processedResult.providerName}` : ''}
                </p>
                {processedResult.patientName && (
                  <p className="text-xs text-slate-400 mb-4">Filed under {processedResult.patientName}</p>
                )}
                <div className="w-full space-y-2">
                  {processedResult.docs?.map((d, i) => (
                    <div key={d.id || i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-left">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-slate-700 font-medium truncate">{d.title || `Document ${i+1}`}</p>
                        <p className="text-[9px] text-slate-400">
                          → {d.bucket || (
                            ['lab_result','imaging_report'].includes(d.category)
                              ? 'Lab/Xray bucket' : 'Correspondence inbox'
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-xl bg-blue-50 border border-blue-100 w-full">
                  <p className="text-[11px] text-blue-700 font-medium">Where to find these:</p>
                  <p className="text-[10px] text-blue-600 mt-0.5">
                    Provider's <strong>My Day → Labs/Imaging</strong> or <strong>My Day → Correspondence</strong> bucket
                  </p>
                </div>
                <button onClick={() => { setProcessedResult(null); onConfirmed(); }}
                  className="mt-4 w-full py-2 rounded-xl bg-slate-900 text-white text-xs font-medium"
                  data-testid="done-close-btn">
                  Close & Refresh Queue
                </button>
              </div>
            )}

            {/* Process All Actions button */}
            <div className="px-4 py-3 border-t border-slate-100 shrink-0 space-y-2">
              {confirmCount > 0 && !processedResult && (
                <button onClick={processAllActions} disabled={processing}
                  className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  data-testid="process-actions-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {processing ? 'Processing...' : `Process ${confirmCount} Action${confirmCount > 1 ? 's' : ''}`}
                </button>
              )}
              {!processedResult && (
                <button onClick={() => setAiResults(null)}
                  className="w-full py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium">
                  Review Document Manually
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 space-y-2">
          {/* Re-run AI — amber/prominent so staff know this is the correction button */}
          <button onClick={handleReprocess} disabled={reprocessing}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100 text-xs font-semibold transition-colors disabled:opacity-60"
            data-testid="overlay-reprocess-footer-btn"
            title="AI extraction wrong? Click to re-analyze this document">
            {reprocessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
            {reprocessing ? 'Analyzing...' : '↻ Re-run AI Analysis'}
          </button>

          {/* Undo split — shown on child documents */}
          {doc.parent_doc_id && (
            <UndoSplitButton parentDocId={doc.parent_doc_id} onUndone={() => { onClose(); onConfirmed(); }} />
          )}

          <div className="flex gap-2">
            {(isMultiPage || aiMultiDoc) && (
              <button onClick={handleSplit} disabled={splitting}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-xl border border-purple-200 text-purple-600 hover:bg-purple-50 text-xs font-medium transition-colors disabled:opacity-60"
                data-testid="overlay-split-btn">
                {splitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                {splitting ? '...' : 'Auto Split'}
              </button>
            )}
            <button onClick={() => setShowManualSplit(true)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs font-medium transition-colors"
              data-testid="overlay-manual-split-btn">
              <FileText className="w-3 h-3" /> Manual Split
            </button>
          </div>
          <Button
            className={`w-full gap-2 transition-all ${allReady ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
            onClick={handleConfirm} disabled={confirming || !allReady}
            data-testid="overlay-confirm-btn"
            title={!allReady ? 'Fill in Document Type, Provider, and Patient to route' : 'Confirm & Route to Provider'}>
            {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {allReady ? 'Confirm & Route to Provider' : `${[!categoryReady && 'type', !providerReady && 'provider', !patientReady && 'patient'].filter(Boolean).join(', ')} needed`}
          </Button>
        </div>
      </div>

      {/* Manual Split Panel — full overlay */}
      {showManualSplit && (
        <ManualSplitPanel
          doc={doc}
          onClose={() => setShowManualSplit(false)}
          onSplit={(childIds) => {
            setShowManualSplit(false);
            toast.success(`Manually split into ${childIds.length} documents`);
            onConfirmed();
          }}
        />
      )}

      {/* Local NewPatient modal — stays INSIDE the overlay, preserves all state */}
      {localNewPatient && (
        <NewPatientPrompt
          docTitle={doc.title}
          extracted={localNewPatient.extracted || {}}
          onCreatePatient={handleLocalCreatePatient}
          onDismiss={() => setLocalNewPatient(null)}
          onClose={() => setLocalNewPatient(null)}
        />
      )}

      {/* RIGHT PANEL — document viewer */}
      <div className="flex-1 flex flex-col bg-slate-900">
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700 shrink-0">
          <p className="text-sm text-slate-300 font-medium">{doc.title}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 relative">
          {pdfLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
            </div>
          )}
          {!pdfLoading && pdfUrl && (
            <iframe src={pdfUrl} className="w-full h-full border-0" title="Document viewer"
              data-testid="doc-pdf-viewer" />
          )}
          {!pdfLoading && !pdfUrl && (
            <div className="h-full overflow-auto p-6">
              <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-4 tracking-wider">Document Content</p>
                <pre className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed font-mono">
                  {doc.ocr_text || doc.ocr_preview || 'No content available'}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   UNDO SPLIT BUTTON
   ═══════════════════════════════════════ */

function UndoSplitButton({ parentDocId, onUndone }) {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleUndo = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/doc-manager/undo-split/${parentDocId}`);
      toast.success(res.data.message || 'Split undone — original restored');
      onUndone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
      setShowConfirm(false);
    } finally { setLoading(false); }
  };

  if (showConfirm) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-50 border border-purple-200">
        <p className="text-[11px] text-purple-700 flex-1">Void all split sections and restore the original PDF?</p>
        <button onClick={handleUndo} disabled={loading}
          className="px-2.5 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-medium disabled:opacity-60"
          data-testid="undo-split-confirm">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes, Undo'}
        </button>
        <button onClick={() => setShowConfirm(false)}
          className="px-2.5 py-1 rounded-lg border border-purple-200 text-purple-600 text-[10px] font-medium">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => setShowConfirm(true)}
      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-purple-200 text-purple-600 hover:bg-purple-50 text-xs font-medium transition-colors"
      data-testid="undo-split-btn">
      <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Undo AI Split (Restore Original)
    </button>
  );
}


/* ═══════════════════════════════════════
   MANUAL SPLIT PANEL
   ═══════════════════════════════════════ */

function ManualSplitPanel({ doc, onClose, onSplit }) {
  const pageCount = doc.page_count || 1;
  const [sections, setSections] = useState([
    { id: 1, start_page: 1, end_page: Math.ceil(pageCount / 2), title: '', category: 'miscellaneous' },
    { id: 2, start_page: Math.ceil(pageCount / 2) + 1, end_page: pageCount, title: '', category: 'miscellaneous' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const addSection = () => {
    const last = sections[sections.length - 1];
    setSections([...sections, { id: Date.now(), start_page: last.end_page + 1, end_page: pageCount, title: '', category: 'miscellaneous' }]);
  };

  const updateSection = (id, field, value) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeSection = (id) => {
    if (sections.length <= 2) return;
    setSections(prev => prev.filter(s => s.id !== id));
  };

  const handleSubmit = async () => {
    for (const s of sections) {
      if (!s.start_page || !s.end_page || s.start_page > s.end_page) {
        toast.error('Check page ranges — each section needs valid start and end pages');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/doc-manager/manual-split/${doc.id}`, sections);
      onSplit(res.data.child_ids || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Manual split failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] overflow-hidden flex flex-col" data-testid="manual-split-panel">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Manual Split — {pageCount} pages</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Define page ranges for each section. Each becomes a separate document.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {sections.map((s, i) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Section {i + 1}</span>
                {sections.length > 2 && (
                  <button onClick={() => removeSection(s.id)} className="text-[9px] text-rose-400 hover:text-rose-600">Remove</button>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[9px] text-slate-400 uppercase">Pages from</label>
                  <input type="number" min={1} max={pageCount}
                    value={s.start_page}
                    onChange={e => updateSection(s.id, 'start_page', parseInt(e.target.value) || 1)}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-[9px] text-slate-400 uppercase">To page</label>
                  <input type="number" min={1} max={pageCount}
                    value={s.end_page}
                    onChange={e => updateSection(s.id, 'end_page', parseInt(e.target.value) || 1)}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-[9px] text-slate-400 uppercase">Type</label>
                  <select value={s.category} onChange={e => updateSection(s.id, 'category', e.target.value)}
                    className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                    {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[9px] text-slate-400 uppercase">Title (optional)</label>
                <input value={s.title} onChange={e => updateSection(s.id, 'title', e.target.value)}
                  placeholder={`Section ${i + 1}`}
                  className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none" />
              </div>
            </div>
          ))}
          <button onClick={addSection}
            className="w-full py-2 rounded-xl border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 text-xs font-medium">
            + Add Another Section
          </button>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-3 bg-slate-50/60 shrink-0">
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="flex-1 gap-1.5 bg-slate-900 hover:bg-slate-800"
            onClick={handleSubmit} disabled={submitting} data-testid="manual-split-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {submitting ? 'Splitting...' : `Create ${sections.length} Documents`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccessControlPanel({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    // Load all users and check their doc manager access
    const loadUsers = async () => {
      try {
        // Get all staff via a simple query
        const res = await api.get('/staff/nurses'); // reuse existing endpoint
        // Also need to get all users — use a broader search
        const allRes = await api.get('/patients?search='); // hack: we need a users endpoint
        // For now, build from known users
        const knownUsers = [
          { email: 'admin@prattos.com', name: 'Admin User (Dr. Smith)', role: 'admin' },
          { email: 'nurse@prattos.com', name: 'Lisa Thompson', role: 'nurse' },
          { email: 'billing@prattos.com', name: 'Billing User', role: 'billing' },
          { email: 'front_desk@prattos.com', name: 'Front Desk', role: 'front_desk' },
        ];

        // Check access for each
        const usersWithAccess = [];
        for (const u of knownUsers) {
          // The roles that have default access
          const hasDefaultAccess = ['admin', 'office_manager', 'front_desk'].includes(u.role);
          usersWithAccess.push({ ...u, has_access: hasDefaultAccess, is_default: hasDefaultAccess });
        }
        // Add nurses from the nurse list
        for (const nurse of (res.data.nurses || [])) {
          if (!usersWithAccess.find(u => u.name === nurse.name)) {
            usersWithAccess.push({ id: nurse.id, email: nurse.email, name: nurse.name, role: 'nurse', has_access: false, is_default: false });
          }
        }
        setUsers(usersWithAccess);
      } catch {} finally { setLoading(false); }
    };
    loadUsers();
  }, []);

  const toggleAccess = async (user, grant) => {
    setToggling(user.email);
    try {
      if (grant) {
        await api.post('/doc-manager/access/grant', { user_id: user.id || user.email });
        toast.success(`${user.name} — access granted`);
      } else {
        await api.post('/doc-manager/access/revoke', { user_id: user.id || user.email });
        toast.success(`${user.name} — access revoked`);
      }
      setUsers(prev => prev.map(u => u.email === user.email ? { ...u, has_access: grant } : u));
    } catch { toast.error('Failed'); }
    finally { setToggling(null); }
  };

  return (
    <div className="mx-6 mb-4 bg-white rounded-2xl border shadow-sm p-4" data-testid="access-control-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>Access Control</h3>
          <p className="text-[10px] text-slate-400">Toggle who can see and use the Clinic Document Manager</p>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
      </div>
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
      ) : (
        <div className="space-y-1.5">
          {users.map(user => (
            <div key={user.email} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-500">
                  {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <span className="text-xs font-medium text-slate-700">{user.name}</span>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[8px] h-3.5">{user.role}</Badge>
                    {user.is_default && <span className="text-[8px] text-slate-400">default access</span>}
                  </div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer"
                  checked={user.has_access}
                  disabled={toggling === user.email}
                  onChange={e => toggleAccess(user, e.target.checked)}
                  data-testid={`toggle-access-${user.email}`} />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function NewPatientPrompt({ docTitle, extracted = {}, onCreatePatient, onDismiss, onClose }) {
  const [firstName, setFirstName] = useState(extracted.firstName || '');
  const [lastName, setLastName] = useState(extracted.lastName || '');
  const [dob, setDob] = useState(extracted.dob || '');
  const [sex, setSex] = useState(extracted.sex || 'M');
  const [phone, setPhone] = useState(extracted.phone || '');
  const [creating, setCreating] = useState(false);
  const hasExtracted = extracted.firstName || extracted.lastName;

  const handleCreate = async () => {
    if (!firstName.trim() || !lastName.trim() || !dob) {
      toast.error('First name, last name, and DOB are required');
      return;
    }
    setCreating(true);
    await onCreatePatient({ first_name: firstName, last_name: lastName, dob, sex, phone });
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" data-testid="new-patient-prompt">
      <div className="bg-white rounded-2xl shadow-2xl w-[460px] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-blue-500" /> Create New Patient Chart
            </h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Document: {docTitle}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {hasExtracted && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <p className="text-[11px] text-blue-700">Fields pre-filled from document — verify before saving</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold">First Name *</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)}
                className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="First name" autoFocus />
            </div>
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold">Last Name *</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)}
                className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Last name" />
            </div>
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold">Date of Birth *</label>
              <input type="date" value={dob} onChange={e => setDob(e.target.value)}
                className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold">Sex</label>
              <select value={sex} onChange={e => setSex(e.target.value)}
                className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[9px] text-slate-400 uppercase font-semibold">Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)}
                className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="555-0100" />
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2 justify-end">
          <Button variant="ghost" size="sm" className="text-xs" onClick={onDismiss}>
            Not Our Patient
          </Button>
          <Button size="sm" className="text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
            Create Chart & Link
          </Button>
        </div>
      </div>
    </div>
  );
}
